import { z } from 'zod';
import { ACCESS_TYPES, CONTENT_MODES, FORM_TYPES, SUBMISSION_DISPLAY_STATUSES, } from '../types/enums.js';
import { TEXT_ALIGNMENTS } from '../utils/rich-text.js';
import { isPdfPlaceable } from '../types/fields.js';
import { fieldKeySchema, isoDateTimeSchema, optionalTrimmedString, trimmedString, } from './common.js';
// ---------------------------------------------------------------------------
// PDF positioning
// ---------------------------------------------------------------------------
const unitInterval = z.number().min(0).max(1);
export const pdfFieldPositionSchema = z
    .object({
    page: z.number().int().min(1).max(2000),
    x: unitInterval,
    y: unitInterval,
    width: z.number().gt(0.002).max(1),
    height: z.number().gt(0.002).max(1),
})
    .refine((p) => p.x + p.width <= 1.0001, {
    message: 'El campo se sale por el borde derecho de la página',
    path: ['width'],
})
    .refine((p) => p.y + p.height <= 1.0001, {
    message: 'El campo se sale por el borde inferior de la página',
    path: ['height'],
});
// ---------------------------------------------------------------------------
// Field configuration, discriminated by field type
// ---------------------------------------------------------------------------
const optionSchema = z.object({
    value: trimmedString(1, 200),
    label: trimmedString(1, 300),
});
const optionsConfig = z
    .object({ options: z.array(optionSchema).min(1).max(100) })
    .refine((c) => new Set(c.options.map((o) => o.value)).size === c.options.length, {
    message: 'Los valores de las opciones no pueden repetirse',
    path: ['options'],
});
const fieldBase = z.object({
    key: fieldKeySchema,
    /**
     * For inputs this is the visible label; for HEADING/PARAGRAPH/INFO it is the
     * text of the block itself, which is why it allows long content.
     */
    label: z.string().trim().max(20000),
    helpText: optionalTrimmedString(1000),
    required: z.boolean().default(false),
    position: pdfFieldPositionSchema.optional(),
});
const field = (type, config) => fieldBase.extend({ type: z.literal(type), config });
const empty = z.object({}).default({});
export const formFieldInputSchema = z.discriminatedUnion('type', [
    // Content ---------------------------------------------------------------
    field('HEADING', z
        .object({
        level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
        align: z.enum(TEXT_ALIGNMENTS).default('left'),
    })
        .default({ level: 2, align: 'left' })),
    field('PARAGRAPH', z.object({ align: z.enum(TEXT_ALIGNMENTS).default('left') }).default({ align: 'left' })),
    field('DIVIDER', empty),
    field('INFO', z
        .object({
        variant: z.enum(['info', 'warning']).default('info'),
        align: z.enum(TEXT_ALIGNMENTS).default('left'),
    })
        .default({ variant: 'info', align: 'left' })),
    // Inputs ----------------------------------------------------------------
    field('SHORT_TEXT', z
        .object({
        placeholder: optionalTrimmedString(200),
        minLength: z.number().int().min(0).max(10000).optional(),
        maxLength: z.number().int().min(1).max(10000).default(500),
    })
        .default({ maxLength: 500 })),
    field('LONG_TEXT', z
        .object({
        placeholder: optionalTrimmedString(200),
        minLength: z.number().int().min(0).max(20000).optional(),
        maxLength: z.number().int().min(1).max(20000).default(5000),
        rows: z.number().int().min(2).max(20).default(4),
    })
        .default({ maxLength: 5000, rows: 4 })),
    field('EMAIL', z
        .object({
        placeholder: optionalTrimmedString(200),
        /** Prefills with the address the respondent identified themselves with. */
        autoFill: z.enum(['NONE', 'RESPONDENT_EMAIL']).default('RESPONDENT_EMAIL'),
    })
        .default({ autoFill: 'RESPONDENT_EMAIL' })),
    field('NUMBER', z
        .object({
        min: z.number().optional(),
        max: z.number().optional(),
        step: z.number().gt(0).optional(),
        placeholder: optionalTrimmedString(200),
    })
        .default({})),
    field('DATE', z
        .object({
        /** Fills the field with the completion date and renders it read-only. */
        autoFill: z.enum(['NONE', 'SIGNING_DATE']).default('NONE'),
        min: z.iso.date().optional(),
        max: z.iso.date().optional(),
    })
        .default({ autoFill: 'NONE' })),
    field('SELECT', optionsConfig),
    field('RADIO', optionsConfig),
    field('CHECKBOX', empty),
    field('MULTI_CHECKBOX', z
        .object({
        options: z.array(optionSchema).min(1).max(100),
        minSelected: z.number().int().min(0).max(100).optional(),
        maxSelected: z.number().int().min(1).max(100).optional(),
    })
        .refine((c) => new Set(c.options.map((o) => o.value)).size === c.options.length, {
        message: 'Los valores de las opciones no pueden repetirse',
        path: ['options'],
    })),
    // Identity helpers ------------------------------------------------------
    field('FULL_NAME', z
        .object({ autoFill: z.enum(['NONE', 'RESPONDENT_NAME']).default('RESPONDENT_NAME') })
        .default({ autoFill: 'RESPONDENT_NAME' })),
    field('INITIALS', empty),
    // Legal acceptance ------------------------------------------------------
    field('ACKNOWLEDGEMENT', empty),
    field('AGREEMENT', empty),
    // Signature -------------------------------------------------------------
    field('SIGNATURE', empty),
]);
// ---------------------------------------------------------------------------
// Version content
// ---------------------------------------------------------------------------
/**
 * The editable content of the draft version of a form.
 *
 * `mode` is fixed when the draft is created (uploading a PDF switches a draft
 * to PDF mode) and the field rules differ per mode:
 *
 *  - NATIVE: fields render as a document inside the app; no positions.
 *  - PDF: input fields are positioned on top of the uploaded PDF. Acceptance
 *    checkboxes are the exception - they render underneath the document and
 *    therefore carry no position.
 */
export const formContentSchema = z
    .object({
    mode: z.enum(CONTENT_MODES),
    fields: z.array(formFieldInputSchema).max(300),
})
    .superRefine((content, ctx) => {
    const seen = new Set();
    content.fields.forEach((f, index) => {
        if (seen.has(f.key)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Nombre de campo repetido: ' + f.key,
                path: ['fields', index, 'key'],
            });
        }
        seen.add(f.key);
        const acceptance = f.type === 'ACKNOWLEDGEMENT' || f.type === 'AGREEMENT';
        if (content.mode === 'NATIVE' && f.position) {
            ctx.addIssue({
                code: 'custom',
                message: 'Los formularios propios no usan posiciones sobre un PDF',
                path: ['fields', index, 'position'],
            });
        }
        if (content.mode === 'PDF') {
            if (!f.position && !acceptance) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Los campos de un formulario PDF deben colocarse sobre una página',
                    path: ['fields', index, 'position'],
                });
            }
            if (f.position && !isPdfPlaceable(f.type)) {
                ctx.addIssue({
                    code: 'custom',
                    message: f.type + ' no se puede colocar sobre una página PDF',
                    path: ['fields', index, 'type'],
                });
            }
        }
        if (f.type === 'MULTI_CHECKBOX') {
            const { minSelected, maxSelected, options } = f.config;
            if (minSelected !== undefined && maxSelected !== undefined && minSelected > maxSelected) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'El mínimo de opciones no puede ser mayor que el máximo',
                    path: ['fields', index, 'config', 'minSelected'],
                });
            }
            if (maxSelected !== undefined && maxSelected > options.length) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'El máximo de opciones supera la cantidad disponible',
                    path: ['fields', index, 'config', 'maxSelected'],
                });
            }
        }
        if (f.type === 'NUMBER') {
            const { min, max } = f.config;
            if (min !== undefined && max !== undefined && min > max) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'El mínimo no puede ser mayor que el máximo',
                    path: ['fields', index, 'config', 'min'],
                });
            }
        }
    });
});
// ---------------------------------------------------------------------------
// Form settings and CRUD payloads
// ---------------------------------------------------------------------------
export const formSettingsSchema = z.object({
    /** Requires a signed-in TacoBots user even when the link is public. */
    requireAuthentication: z.boolean().default(false),
    /** Ask the respondent for name and email before opening the document. */
    requireEmail: z.boolean().default(true),
    /** Reserved for the OTP / magic-link flow (see docs/architecture.md). */
    requireEmailVerification: z.boolean().default(false),
    allowMultipleSubmissionsPerEmail: z.boolean().default(false),
    closesAt: isoDateTimeSchema.nullable().default(null),
    maxSubmissions: z.number().int().min(1).max(1000000).nullable().default(null),
});
export const createFormSchema = z.object({
    title: trimmedString(1, 200),
    description: optionalTrimmedString(2000),
    internalName: optionalTrimmedString(120),
    type: z.enum(FORM_TYPES).default('FORM'),
    accessType: z.enum(ACCESS_TYPES).default('PUBLIC_LINK'),
    contentMode: z.enum(CONTENT_MODES).default('NATIVE'),
});
export const updateFormSchema = z.object({
    title: trimmedString(1, 200).optional(),
    description: optionalTrimmedString(2000).nullable().optional(),
    internalName: optionalTrimmedString(120).nullable().optional(),
    type: z.enum(FORM_TYPES).optional(),
    accessType: z.enum(ACCESS_TYPES).optional(),
    settings: formSettingsSchema.partial().optional(),
});
/**
 * How a new version starts.
 *
 * `CURRENT` copies the published version, which is the usual case: a small
 * correction to something that already works. `BLANK` starts empty, and is what
 * makes it possible to move a form from a hand-built layout to an uploaded PDF
 * (or back) without losing a single signed document - the old versions stay
 * exactly as they were, and the submissions keep pointing at them.
 */
export const createDraftSchema = z
    .object({
    source: z.enum(['CURRENT', 'BLANK']).default('CURRENT'),
    /** Only meaningful with `BLANK`; a copy keeps the mode it is copying. */
    contentMode: z.enum(CONTENT_MODES).optional(),
})
    // Sending no body at all means "the usual": copy what is published.
    .default({ source: 'CURRENT' });
/**
 * Switches the authoring mode of a draft that is not published yet.
 *
 * Existing versions are untouched, so a form can move from a hand-built layout
 * to an uploaded PDF - or back - while every document already signed keeps
 * rendering from the version it was signed against.
 */
export const draftModeSchema = z.object({
    contentMode: z.enum(CONTENT_MODES),
});
export const listFormsQuerySchema = z.object({
    search: optionalTrimmedString(200),
    status: z.enum(['DRAFT', 'PUBLISHED', 'CLOSED', 'ARCHIVED']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const listSubmissionsQuerySchema = z.object({
    search: optionalTrimmedString(200),
    // Filtered by what the list shows, not by what the column stores:
    // IDENTIFIED_ONLY and STARTED are both stored as STARTED.
    status: z.enum(SUBMISSION_DISPLAY_STATUSES).optional(),
    versionId: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=form.js.map